<template>
  <!--
    Barra global de progreso de navegación (c398).

    Antes de esto, #app era un shell vacío: durante el arranque el guard de
    router espera authStore.initialize() (timeout axios 30s + escalera de
    reintentos) + tenantStore.initializeFromSlug() ANTES de renderizar la vista
    destino → carga en frío durante un deploy = página en blanco sin feedback
    (observado vivo en c397). En SPA, navegaciones lentas (móvil) también se
    veían como "no pasó nada".

    Delay de 150 ms antes de aparecer: las navegaciones instantáneas nunca
    muestran la barra (cero parpadeo). Mínimo visible 300 ms al mostrarla.
  -->
  <div
    class="app-loading-bar"
    :class="{ 'app-loading-bar--active': isVisible }"
    role="progressbar"
    aria-label="Cargando página"
    :aria-hidden="!isVisible"
  >
    <div class="app-loading-bar__fill" />
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const SHOW_DELAY_MS = 150
const MIN_VISIBLE_MS = 300

const router = useRouter()
const isVisible = ref(false)

let pendingCount = 0
let showTimer: ReturnType<typeof setTimeout> | undefined
let hideTimer: ReturnType<typeof setTimeout> | undefined
let shownAt = 0

const clearTimers = () => {
  if (showTimer) clearTimeout(showTimer)
  if (hideTimer) clearTimeout(hideTimer)
  showTimer = hideTimer = undefined
}

const onNavigationStart = () => {
  pendingCount++
  if (pendingCount > 1) return
  clearTimers()
  showTimer = setTimeout(() => {
    isVisible.value = true
    shownAt = Date.now()
  }, SHOW_DELAY_MS)
}

const onNavigationEnd = () => {
  pendingCount = Math.max(0, pendingCount - 1)
  if (pendingCount > 0) return
  clearTimers()
  if (!isVisible.value) return
  // Navegación terminada: respetar el mínimo visible para no parpadear.
  const elapsed = Date.now() - shownAt
  hideTimer = setTimeout(() => {
    isVisible.value = false
  }, Math.max(0, MIN_VISIBLE_MS - elapsed))
}

// Redirecciones anidadas disparan beforeEach otra vez: el contador soporta el
// solape. onError cubre guards que lanzan (ahí afterEach no corre).
const unsubscribers = [
  router.beforeEach(onNavigationStart),
  router.afterEach(onNavigationEnd),
  router.onError(onNavigationEnd),
]

onUnmounted(() => {
  unsubscribers.forEach((unsub) => unsub())
  clearTimers()
})
</script>

<style scoped>
.app-loading-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

/* Fade con clases, no <Transition>: la resolución de leave/enter de Vue
   depende de requestAnimationFrame, no determinista bajo fake timers. */
.app-loading-bar--active {
  opacity: 1;
  visibility: visible;
}

.app-loading-bar__fill {
  height: 100%;
  width: 40%;
  background-color: #8ac546; /* brand-500 */
  border-radius: 0 2px 2px 0;
  box-shadow: 0 0 6px rgba(138, 197, 70, 0.6);
  animation: app-loading-slide 1s ease-in-out infinite;
}

@keyframes app-loading-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}
</style>
